/**
 * PATH: packages/ui/src/kanban/components/TriageBulkBar.tsx
 * FILE_ID: QUI-KANBAN-TRIAGE-BULK-001
 * PURPOSE: "Accept all (N) / Reject all (N)" bulk-action bar that
 *          renders above the card list when the operator filters a
 *          Triage / Inbox-Private lane down to the AI bucket.
 * WHY: Module D EMAIL_KANBAN_ALLOCATION_PLAN.md follow-up. When a
 *      lane has 10+ AI suggestions queued (e.g. after a bulk import
 *      or an overnight classifier run), reviewing them one-by-one
 *      via the per-card ✓/✗ band is tedious. This bar lets the
 *      operator sweep the bucket in one gesture.
 *
 * FLOW:
 *   ┌──────────────┐    ┌─────────────────────┐    ┌──────────────────────┐
 *   │ AI cards (N) │ →  │ Accept all / Reject │ →  │ N × confirmAi /      │
 *   │ in lane      │    │ all click           │    │ rejectAiAllocation   │
 *   └──────────────┘    └─────────────────────┘    └──────────────────────┘
 *
 * Pure presentation + glue. Network calls are owned by the caller;
 * this component just wires the buttons to the supplied handlers and
 * confirms first. Caller is responsible for invalidating cards cache
 * after the batch completes.
 *
 * DEPENDENCIES:
 *   - lucide-react: Check, X icons (already used across the kanban
 *     package — no new dep)
 *
 * RELATED:
 *   - components/KanbanLane.tsx — rendered there inside the existing
 *     `showUcAiTabs && triageTab === 'ai'` branch
 *   - components/CardAiBand.tsx — per-card single-allocation version
 *     of the same UX
 */

import { useState } from 'react'
import { Check, X as XIcon } from 'lucide-react'

export interface TriageBulkBarProps {
  /** Number of AI-pending cards currently visible. Drives the
   *  badge counts and disables both buttons when 0. */
  count: number
  /** Confirm every visible AI suggestion. Caller is responsible
   *  for: (a) confirming with the operator if required (the bar
   *  shows a 2-step confirm itself), (b) running confirmAiAllocation
   *  for each allocation, (c) invalidating the cards cache. */
  onAcceptAll: () => Promise<void> | void
  /** Reject every visible AI suggestion. Same caller responsibilities
   *  as onAcceptAll. */
  onRejectAll: () => Promise<void> | void
  /** When true, the buttons are disabled (a batch is in flight). The
   *  caller flips this while the batch runs to prevent double-clicks. */
  disabled?: boolean
}

/**
 * Two-step destructive UX: the first click flips the corresponding
 * button into a "confirm" state with an outlined warning style; the
 * second click within the same render commits. Clicking outside the
 * bar or pressing the other button cancels. This is the same gesture
 * Trello / Linear use for bulk destructive actions and matches the
 * existing CardAiBand semantics (per-card buttons commit immediately
 * because each one only affects a single card — the bulk version
 * deserves a guard because it can wipe 50+ suggestions in one go).
 */
export function TriageBulkBar({
  count,
  onAcceptAll,
  onRejectAll,
  disabled,
}: TriageBulkBarProps) {
  const [pendingConfirm, setPendingConfirm] = useState<'accept' | 'reject' | null>(null)
  const [busy, setBusy] = useState(false)

  if (count <= 0) return null

  const isDisabled = disabled || busy

  const handleAccept = async () => {
    if (pendingConfirm !== 'accept') {
      setPendingConfirm('accept')
      return
    }
    setBusy(true)
    try {
      await onAcceptAll()
    } finally {
      setBusy(false)
      setPendingConfirm(null)
    }
  }

  const handleReject = async () => {
    if (pendingConfirm !== 'reject') {
      setPendingConfirm('reject')
      return
    }
    setBusy(true)
    try {
      await onRejectAll()
    } finally {
      setBusy(false)
      setPendingConfirm(null)
    }
  }

  return (
    <div
      role="toolbar"
      aria-label="Bulk AI allocation actions"
      data-testid="triage-bulk-bar"
      className="flex items-center justify-between gap-2 border-b border-amber-300/60 bg-amber-50/70 px-2 py-1 text-[11px] dark:border-amber-700/50 dark:bg-amber-950/30"
    >
      <span className="font-medium text-amber-900 dark:text-amber-200">
        {count} AI suggestion{count === 1 ? '' : 's'}
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={handleAccept}
          disabled={isDisabled}
          aria-label={pendingConfirm === 'accept' ? 'Confirm accept all' : 'Accept all AI suggestions'}
          title={pendingConfirm === 'accept' ? 'Click again to confirm' : 'Accept all'}
          className={[
            'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-medium transition-colors',
            'disabled:cursor-not-allowed disabled:opacity-50',
            pendingConfirm === 'accept'
              ? 'bg-emerald-600 text-white ring-2 ring-emerald-300 dark:ring-emerald-800'
              : 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:hover:bg-emerald-900/70',
          ].join(' ')}
        >
          <Check className="h-3 w-3" />
          {pendingConfirm === 'accept' ? `Confirm (${count})` : `Accept all`}
        </button>
        <button
          type="button"
          onClick={handleReject}
          disabled={isDisabled}
          aria-label={pendingConfirm === 'reject' ? 'Confirm reject all' : 'Reject all AI suggestions'}
          title={pendingConfirm === 'reject' ? 'Click again to confirm' : 'Reject all'}
          className={[
            'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-medium transition-colors',
            'disabled:cursor-not-allowed disabled:opacity-50',
            pendingConfirm === 'reject'
              ? 'bg-red-600 text-white ring-2 ring-red-300 dark:ring-red-800'
              : 'bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-900/40 dark:text-red-200 dark:hover:bg-red-900/70',
          ].join(' ')}
        >
          <XIcon className="h-3 w-3" />
          {pendingConfirm === 'reject' ? `Confirm (${count})` : `Reject all`}
        </button>
      </div>
    </div>
  )
}
